import { Box, Button, IconButton } from "@chakra-ui/react";
import {
  MILLISECONDS_IN_DAY,
  MILLISECONDS_IN_HOUR,
  Activity,
  RoleType,
  Account,
} from "common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MdNavigateBefore, MdNavigateNext } from "react-icons/md";
import { CalendarTimeDivider } from "./CalendarTimeDivider";
import {
  DAYS_OF_WEEK_SHORT,
  getStartOfDay,
  getStartOfToday,
} from "../utils/time";
import { ActivityCard } from "./ActivityCard";
import { SelectedActivityFloatingLayer } from "./SelectedActivityFloatingLayer";
import { CreateActivityFloatingLayer } from "./CreateActivityFloatingLayer";
import { useIsInRole } from "../utils/useIsInRole";
import { IoIosAdd } from "react-icons/io";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { PAGE_BODY_HEIGHT } from "../utils/AppPage";
import { CurrentCalendarTime } from "./CurrentCalendarTime";

const HOUR_HEIGHT = "7rem";
const TIME_WIDTH = "4rem";
const DATE_HEADER_HEIGHT = "4rem";
const CALENDAR_PADDING_TOP = "2rem";
const CALENDAR_PADDING_SIDE = "1rem";

interface CalendarProps {
  activityList: Activity[];
  onRefetchList?: () => void;
  accounts: Account[];
}

export const Calendar = ({
  activityList,
  onRefetchList,
  accounts,
}: CalendarProps) => {
  const [startOfDayMs, setStartOfDayMs] = useState(getStartOfToday());
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(
    null
  );
  const [openCreateActivity, setOpenCreateActivity] = useState(false);
  const isAdmin = useIsInRole(RoleType.Admin);
  const [editActivityId, setEditActivityId] = useState<number | null>(null);
  const [haveInteracted, setHaveInteracted] = useState(false);
  const [initialCreateValues, setInitialCreateValues] = useState<
    Partial<Activity>
  >({});

  // Start of clamp time
  const firstStartTimeMs = useMemo(() => {
    if (activityList.length === 0) {
      return null;
    }
    const firstActivity = activityList.reduce((prev, curr) =>
      !prev ? curr : prev.startTime < curr.startTime ? prev : curr
    );
    return getStartOfDay(firstActivity.startTime);
  }, [activityList]);

  const lastEndTimeMs = useMemo(() => {
    if (activityList.length === 0) {
      return null;
    }
    const lastActivity = activityList.reduce((prev, curr) =>
      !prev ? curr : prev.endTime > curr.endTime ? prev : curr
    );
    return getStartOfDay(lastActivity.startTime);
  }, [activityList]);

  const selectedActivity = useMemo(
    () => activityList.find((a) => a.id === selectedActivityId),
    [activityList, selectedActivityId]
  );

  const endOfDayMs = useMemo(
    () => startOfDayMs + MILLISECONDS_IN_DAY,
    [startOfDayMs]
  );

  const currentDateString = useMemo(() => {
    const startOfDayDate = new Date(startOfDayMs);
    const month = startOfDayDate.toLocaleString("default", { month: "short" });
    return `${
      DAYS_OF_WEEK_SHORT[startOfDayDate.getDay()]
    } ${startOfDayDate.getDate()} ${month}`;
  }, [startOfDayMs]);

  /**
   * Todays activities sorted by start time
   */
  const todaysActivities = useMemo(
    () =>
      activityList
        .filter(
          (a) =>
            (a.startTime <= endOfDayMs && a.startTime >= startOfDayMs) ||
            (a.endTime <= endOfDayMs && a.endTime >= startOfDayMs)
        )
        .sort((a1, a2) => (a1.startTime < a2.startTime ? -1 : 1)),
    [activityList, endOfDayMs, startOfDayMs]
  );

  const displayTime = useCallback((hour: number) => {
    return `${hour === 0 || hour === 12 ? 12 : hour % 12}${
      hour < 12 ? "AM" : "PM"
    }`;
  }, []);

  useEffect(() => {
    if (!lastEndTimeMs || !firstStartTimeMs || haveInteracted) {
      return;
    }
    const todayMs = getStartOfToday();
    setStartOfDayMs(
      Math.max(firstStartTimeMs, Math.min(todayMs, lastEndTimeMs))
    );
  }, [firstStartTimeMs, haveInteracted, lastEndTimeMs]);

  return (
    <Box
      display={"flex"}
      width={"100%"}
      flexDir={"column"}
      position={"relative"}
    >
      {isAdmin ? (
        <CreateActivityFloatingLayer
          editActivityId={editActivityId}
          isOpen={openCreateActivity}
          onClose={() => {
            setEditActivityId(null);
            setOpenCreateActivity(false);
          }}
          onSubmitSuccess={onRefetchList}
          initialValues={initialCreateValues}
        />
      ) : null}
      <SelectedActivityFloatingLayer
        selectedActivity={selectedActivity}
        isOpen={Boolean(selectedActivity)}
        onClose={() => setSelectedActivityId(null)}
        onEdit={(activityId) => {
          setSelectedActivityId(null);
          setEditActivityId(activityId);
          setInitialCreateValues({});
          setOpenCreateActivity(true);
        }}
        onDeleteSuccess={onRefetchList}
      />
      {isAdmin && !openCreateActivity ? (
        <IconButton
          boxShadow={"base"}
          zIndex={99}
          size={"lg"}
          colorScheme={THEME_COLOR_SCHEME}
          borderRadius={"full"}
          icon={<IoIosAdd />}
          aria-label="Create Activity"
          position={"absolute"}
          bottom={2}
          right={2}
          onClick={() => {
            setInitialCreateValues({});
            setOpenCreateActivity(true);
          }}
        />
      ) : null}
      <Box
        display={"flex"}
        width={"100%"}
        justifyContent={"space-between"}
        height={DATE_HEADER_HEIGHT}
        paddingLeft={CALENDAR_PADDING_SIDE}
        paddingRight={CALENDAR_PADDING_SIDE}
        alignItems={"center"}
      >
        <Button
          variant={"outline"}
          onClick={() => {
            setStartOfDayMs((ms) => getStartOfDay(ms - MILLISECONDS_IN_DAY));
            setHaveInteracted(true);
          }}
        >
          <MdNavigateBefore size={30} />
        </Button>
        <Box fontWeight={"bold"} fontSize={"1.5rem"} opacity={"0.7"}>
          {currentDateString}
        </Box>
        <Button
          variant={"outline"}
          onClick={() => {
            setStartOfDayMs((ms) => getStartOfDay(ms + MILLISECONDS_IN_DAY));
            setHaveInteracted(true);
          }}
        >
          <MdNavigateNext size={30} />
        </Button>
      </Box>
      <Box
        paddingTop={CALENDAR_PADDING_TOP}
        position={"relative"}
        height={`calc(${PAGE_BODY_HEIGHT} - ${DATE_HEADER_HEIGHT})`}
        overflow={"auto"}
        paddingLeft={CALENDAR_PADDING_SIDE}
        paddingRight={CALENDAR_PADDING_SIDE}
      >
        {
          // Render time blocks
          Array.from(Array(24).keys()).map((_, i) => (
            <Box
              key={i}
              height={HOUR_HEIGHT}
              position={"relative"}
              onClick={() => {
                if (!isAdmin) {
                  return;
                }
                setInitialCreateValues({
                  startTime: startOfDayMs + i * MILLISECONDS_IN_HOUR,
                  endTime: startOfDayMs + (i + 1) * MILLISECONDS_IN_HOUR,
                });
                setOpenCreateActivity(true);
              }}
            >
              <CalendarTimeDivider
                time={displayTime(i)}
                timeWidth={TIME_WIDTH}
                top={0}
                transform={"translateY(-50%)"}
              />
            </Box>
          ))
        }
        <CurrentCalendarTime
          startOfCalendarDayMs={startOfDayMs}
          timeWidth={TIME_WIDTH}
          hourHeight={HOUR_HEIGHT}
          paddingLeft={CALENDAR_PADDING_SIDE}
          paddingRight={CALENDAR_PADDING_SIDE}
          calendarPaddingTop={CALENDAR_PADDING_TOP}
        />
        {
          // Render activities
          todaysActivities.map((activity) => (
            <ActivityCard
              key={activity.id}
              accounts={accounts}
              activity={activity}
              position={"absolute"}
              left={`calc(${TIME_WIDTH} + ${CALENDAR_PADDING_SIDE})`}
              top={`calc(${HOUR_HEIGHT} * ${
                Math.max(activity.startTime - startOfDayMs, 0) /
                MILLISECONDS_IN_HOUR
              } + ${CALENDAR_PADDING_TOP})`}
              height={`calc(${HOUR_HEIGHT} * ${
                (activity.endTime - activity.startTime) / MILLISECONDS_IN_HOUR
              })`}
              right={CALENDAR_PADDING_SIDE}
              cursor={"pointer"}
              onClick={(e) => {
                setSelectedActivityId(activity.id);
                e.stopPropagation();
              }}
            />
          ))
        }
        <CalendarTimeDivider time={displayTime(0)} timeWidth={TIME_WIDTH} />
      </Box>
    </Box>
  );
};
